import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiEdit2, FiCamera } from 'react-icons/fi';
import profileService from '../services/profile.service';
import { useAuth } from '../context/AuthContext';

const PROFILE_FIELDS = [
  {
    key: 'full_name',
    label: 'Full Name',
    type: 'text',
    placeholder: 'Add your full name',
  },
  {
    key: 'email',
    label: 'Email',
    type: 'email',
    placeholder: 'Add your email (optional)',
    required: false,
  },
  {
    key: 'gender',
    label: 'Gender',
    type: 'select',
    options: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' },
    ],
    placeholder: 'Select your gender',
  },
  {
    key: 'dob',
    altKeys: ['date_of_birth', 'dateOfBirth'],
    payloadKey: 'date_of_birth',
    label: 'Date of Birth',
    type: 'date',
    placeholder: 'Select your birth date',
  },
  {
    key: 'class_level',
    label: 'Class',
    type: 'select',
    options: [
      { value: 'Class 8', label: 'Class 8' },
      { value: 'Class 9', label: 'Class 9' },
      { value: 'Class 10', label: 'Class 10' },
      { value: 'Class 11', label: 'Class 11' },
      { value: 'Class 12', label: 'Class 12' },
      { value: 'Dropper', label: 'Dropper' },
    ],
    placeholder: 'Select Class',
  },
];

const normaliseOptions = (options = []) => options.map((option) => {
  if (typeof option === 'string') {
    return { value: option, label: option };
  }
  const { value, label } = option;
  return {
    value: value !== undefined ? value : (label || ''),
    label: label || value || '',
  };
});

const getProfileValue = (profile, field) => {
  if (!profile) {
    return '';
  }
  const keys = [field.key, ...(field.altKeys || [])];
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    const value = profile[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return '';
};

const formatDateForDisplay = (value) => {
  if (!value) {
    return '';
  }

  const input = typeof value === 'string' ? value : String(value);
  const trimmed = input.trim();
  if (!trimmed) {
    return '';
  }

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return trimmed;
  }

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const formatDateForInput = (value) => {
  if (!value) {
    return '';
  }
  const input = typeof value === 'string' ? value : String(value);
  if (!input) {
    return '';
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return input;
  }
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const buildDisplayValue = (field, profile) => {
  const raw = getProfileValue(profile, field);
  if (!raw) {
    return '';
  }

  if (field.type === 'date') {
    return formatDateForDisplay(raw);
  }

  if (field.type === 'select') {
    const options = normaliseOptions(field.options);
    const lowerRaw = String(raw).toLowerCase();
    const match = options.find((option) => {
      return String(option.value).toLowerCase() === lowerRaw || String(option.label).toLowerCase() === lowerRaw;
    });
    return match ? match.label : raw;
  }

  return raw;
};

const prepareInputValue = (field, profile) => {
  const raw = getProfileValue(profile, field);
  if (!raw) {
    return '';
  }

  if (field.type === 'date') {
    return formatDateForInput(raw);
  }

  if (field.type === 'select') {
    const options = normaliseOptions(field.options);
    const lowerRaw = String(raw).toLowerCase();
    const match = options.find((option) => {
      return String(option.value).toLowerCase() === lowerRaw || String(option.label).toLowerCase() === lowerRaw;
    });
    return match ? match.value : raw;
  }

  return String(raw);
};

const sanitiseValue = (field, value) => {
  if (value === null || value === undefined) {
    return null;
  }

  let nextValue = value;

  if (typeof nextValue === 'string') {
    nextValue = field.type === 'date' ? nextValue : nextValue.trim();
  }

  if (field.sanitize) {
    nextValue = field.sanitize(String(nextValue));
  }

  if (field.type === 'select') {
    return nextValue || null;
  }

  if (!nextValue) {
    return null;
  }

  return nextValue;
};

const mergeProfilePatch = (profile, field, patch) => {
  const next = { ...(profile || {}) };
  const payloadKey = field.payloadKey || field.key;
  if (patch && typeof patch === 'object' && Object.keys(patch).length > 0) {
    Object.assign(next, patch);
  } else {
    next[payloadKey] = patch;
  }

  if (field.altKeys && field.altKeys.length > 0) {
    field.altKeys.forEach((key) => {
      if (next[key] === undefined) {
        next[key] = next[payloadKey];
      }
    });
  }

  return next;
};

const ProfilePage = () => {
  const { user, logout, updateUserData } = useAuth();
  const [profile, setProfile] = useState(() => (user?.user || user || null));
  const [loading, setLoading] = useState(true);
  const [pageStatus, setPageStatus] = useState('');
  const [pageError, setPageError] = useState('');
  const [editingField, setEditingField] = useState('');
  const [pendingValue, setPendingValue] = useState('');
  const [savingField, setSavingField] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [isPhotoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
  const [modalError, setModalError] = useState('');

  const safeProfile = useMemo(() => profile || {}, [profile]);
  const authProfile = useMemo(() => user?.user || user || {}, [user]);
  const userId = useMemo(() => {
    return authProfile?.id || authProfile?.user_id || authProfile?.userId || null;
  }, [authProfile]);
  const lastFetchedUserRef = useRef(null);
  const fileInputRef = useRef(null);
  const profileName = safeProfile.full_name || safeProfile.name || safeProfile.email || 'Learner';
  const profileEmail = safeProfile.email || 'Email not provided';
  const profileMobile = safeProfile.mobile || safeProfile.phone || '';
  const profilePhoto = safeProfile.photo || safeProfile.avatar || '';
  const initials = useMemo(() => {
    return profileName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'SA';
  }, [profileName]);

  useEffect(() => {
    let isMounted = true;

    if (!userId) {
      setLoading(false);
      return undefined;
    }

    if (lastFetchedUserRef.current === userId) {
      return undefined;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await profileService.getProfile(userId);
        if (!isMounted) {
          return;
        }
        lastFetchedUserRef.current = userId;
        setProfile((prev) => ({ ...(prev || {}), ...(data || {}) }));
        updateUserData(data || {});
        setPageError('');
      } catch (error) {
        if (!isMounted) {
          return;
        }
        const status = error?.response?.status;
        if (status === 401) {
          logout();
          return;
        }
        lastFetchedUserRef.current = null;
        setPageError('We could not load your profile. Please try again.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      isMounted = false;
      lastFetchedUserRef.current = null;
    };
  }, [logout, updateUserData, userId]);

  useEffect(() => {
    return () => {
      if (photoPreviewUrl) {
        URL.revokeObjectURL(photoPreviewUrl);
      }
    };
  }, [photoPreviewUrl]);

  const beginEditing = (fieldKey) => {
    setEditingField(fieldKey);
    const field = PROFILE_FIELDS.find((item) => item.key === fieldKey);
    setPendingValue(field ? prepareInputValue(field, safeProfile) : '');
    setPageStatus('');
    setPageError('');
  };

  const cancelEditing = () => {
    setEditingField('');
    setPendingValue('');
  };

  const resetPhotoSelection = () => {
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setPhotoPreviewUrl('');
    setSelectedPhotoFile(null);
    setModalError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const closePhotoModal = () => {
    setPhotoModalOpen(false);
    resetPhotoSelection();
  };

  const openPhotoModal = () => {
    if (photoUploading) {
      return;
    }
    setPageError('');
    setPageStatus('');
    setModalError('');
    resetPhotoSelection();
    setPhotoModalOpen(true);
  };

  const handlePhotoSelect = (event) => {
    const [file] = event.target.files || [];

    if (!file) {
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setModalError('Please choose a JPG, PNG, or WEBP image.');
      resetPhotoSelection();
      return;
    }

    const maxSizeBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setModalError('Profile photo must be under 5 MB.');
      resetPhotoSelection();
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }

    setPhotoPreviewUrl(previewUrl);
    setSelectedPhotoFile(file);
    setModalError('');
  };

  const handlePhotoUpload = async () => {
    if (!selectedPhotoFile) {
      setModalError('Choose an image before uploading.');
      return;
    }

    const maxSizeBytes = 5 * 1024 * 1024;
    if (selectedPhotoFile.size > maxSizeBytes) {
      setModalError('Profile photo must be under 5 MB.');
      return;
    }

    if (!userId) {
      setPageError('We could not determine your account id. Please sign in again.');
      return;
    }

    try {
  setPhotoUploading(true);
  setModalError('');
  setPageError('');
  setPageStatus('Uploading profile photo...');

      const response = await profileService.updateProfilePhoto(userId, selectedPhotoFile);
      const patch = response && Object.keys(response).length > 0 ? response : { photo: response?.photo };

      setProfile((prev) => ({ ...(prev || {}), ...(patch || {}) }));
      updateUserData(patch || {});
      setPageStatus('Profile photo updated successfully.');
      closePhotoModal();
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        logout();
        return;
      }
      const detail = error?.response?.data?.detail;
      const detailMessage = Array.isArray(detail) ? detail.join(' ') : detail;
      setModalError(detailMessage || 'We could not update your profile photo. Please try again.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSaveField = async () => {
    if (!editingField) {
      return;
    }
    const field = PROFILE_FIELDS.find((item) => item.key === editingField);
    if (!field) {
      cancelEditing();
      return;
    }

    const payloadKey = field.payloadKey || field.key;
    const formattedValue = sanitiseValue(field, pendingValue);
    const currentValue = sanitiseValue(field, prepareInputValue(field, safeProfile));

    if (formattedValue === currentValue) {
      setPageStatus('No changes to save for this field.');
      cancelEditing();
      return;
    }

    if (!userId) {
      setPageError('We could not determine your account id. Please sign in again.');
      cancelEditing();
      return;
    }

    try {
      setSavingField(true);
      setPageStatus('');
      setPageError('');

      const response = await profileService.updateProfileField(userId, payloadKey, formattedValue);
      const patch = response && Object.keys(response).length > 0
        ? response
        : { [payloadKey]: formattedValue };

      setProfile((prev) => mergeProfilePatch(prev, field, patch));
      updateUserData(patch);

      setPageStatus(`${field.label} updated successfully.`);
      cancelEditing();
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        logout();
        return;
      }
      const detail = error?.response?.data?.detail;
      const detailMessage = Array.isArray(detail) ? detail.join(' ') : detail;
      setPageError(detailMessage || `We couldn't update ${field.label.toLowerCase()}. Please try again.`);
    } finally {
      setSavingField(false);
    }
  };

  const renderFieldControl = (field) => {
    const isEditing = editingField === field.key;

    if (!isEditing) {
      const value = buildDisplayValue(field, safeProfile);
      const isMobileField = field.key === 'mobile';
      return (
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-text-secondary uppercase tracking-wide">{field.label}</p>
            <p className="text-lg text-text-primary mt-1">{value || <span className="text-text-secondary">Not added yet</span>}</p>
          </div>
          {!isMobileField && (
            <button
              type="button"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-border text-primary hover:bg-primary/5"
              onClick={() => beginEditing(field.key)}
              aria-label={`Edit ${field.label}`}
            >
              <FiEdit2 className="h-4 w-4" />
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <label className="block text-sm font-semibold text-text-secondary uppercase tracking-wide" htmlFor={`profile-${field.key}`}>
          {field.label}
        </label>
        {field.type === 'select' ? (
          <select
            id={`profile-${field.key}`}
            value={pendingValue || ''}
            onChange={(event) => setPendingValue(event.target.value)}
            className="input-field"
            disabled={field.disabled}
          >
            <option value="">Select</option>
            {normaliseOptions(field.options).map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        ) : (
          <input
            id={`profile-${field.key}`}
            type={field.type}
            value={pendingValue || ''}
            onChange={(event) => setPendingValue(event.target.value)}
            className="input-field"
            placeholder={field.placeholder}
            disabled={field.disabled}
          />
        )}
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-primary"
            onClick={handleSaveField}
            disabled={savingField}
          >
            {savingField ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" className="btn-secondary" onClick={cancelEditing} disabled={savingField}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-bg-default pb-16">
      <div className="page-container pt-10">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="card p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center text-2xl font-semibold text-primary overflow-hidden">
                {profilePhoto ? (
                  <img src={profilePhoto} alt={profileName} className="h-full w-full object-cover" />
                ) : (
                  initials
                )}
                <button
                  type="button"
                  onClick={openPhotoModal}
                  className="absolute bottom-0 right-0 inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow-soft hover:bg-primary/90"
                  aria-label="Edit profile photo"
                  disabled={photoUploading}
                >
                  <FiCamera className="h-4 w-4" />
                </button>
              </div>
              <div>
                <p className="text-2xl font-bold text-primary">{profileName}</p>
                <p className="text-sm text-text-secondary">{profileMobile || 'Mobile not provided'}</p>
              </div>
            </div>
            <div className="flex flex-col sm:items-end gap-2">
              {/* removed informational labels as requested */}
            </div>
          </div>

          <div className="card p-6 space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-primary">Personal Information</h2>
              <p className="text-sm text-text-secondary mt-1">Edit each field separately. Changes are saved instantly once you confirm.</p>
            </div>
            <div className="rounded-lg border border-border px-5 py-4 bg-surface/70">
              <p className="text-sm font-semibold text-text-secondary uppercase tracking-wide">Mobile Number</p>
              <p className="text-lg text-text-primary mt-1 break-words">{profileMobile || 'Not added yet'}</p>
              <p className="text-xs text-text-secondary mt-1">Your mobile acts as the primary login id and cannot be changed.</p>
            </div>

            {pageError && (
              <div className="rounded-md border border-error/30 bg-error/10 px-4 py-3 text-sm text-error">
                {pageError}
              </div>
            )}

            {pageStatus && (
              <div className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary">
                {pageStatus}
              </div>
            )}

            {loading ? (
              <div className="space-y-4">
                {Array.from({ length: PROFILE_FIELDS.length }, (_, index) => index).map((item) => (
                  <div key={item} className="h-20 rounded-lg bg-muted-gray/10 animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                {PROFILE_FIELDS.map((field) => (
                  <div key={field.key} className="rounded-lg border border-border px-5 py-4 bg-surface/70">
                    {renderFieldControl(field)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {isPhotoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="relative w-full max-w-lg rounded-2xl bg-surface p-6 shadow-glow">
            <button
              type="button"
              onClick={closePhotoModal}
              className="absolute top-4 right-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/5 text-primary hover:bg-primary/10"
              aria-label="Close photo editor"
              disabled={photoUploading}
            >
              <span aria-hidden="true">×</span>
            </button>
            <h2 className="text-xl font-semibold text-primary mb-1">Update profile photo</h2>
            <p className="text-sm text-text-secondary mb-5">JPG/PNG/WEBP — max 5 MB</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="flex items-center justify-center">
                <div className="h-28 w-28 overflow-hidden rounded-full bg-primary/10 flex items-center justify-center">
                  {photoPreviewUrl ? (
                    <img src={photoPreviewUrl} alt="Selected preview" className="h-full w-full object-cover" />
                  ) : profilePhoto ? (
                    <img src={profilePhoto} alt={profileName} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-2xl font-semibold text-primary">{initials}</span>
                  )}
                </div>
              </div>
              <div className="flex-1 space-y-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoSelect}
                />
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={photoUploading}
                  >
                    Choose image
                  </button>
                  {selectedPhotoFile && (
                    <div className="flex flex-col">
                      <span className="text-xs text-text-secondary break-all">{selectedPhotoFile.name}</span>
                      {modalError && (
                        <span className="text-xs text-error mt-1">{modalError}</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handlePhotoUpload}
                    disabled={photoUploading}
                  >
                    {photoUploading ? 'Saving…' : 'Save photo'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={closePhotoModal}
                    disabled={photoUploading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
